import { COMMUNICATION_TAGS, type CommunicationTag } from '../types';

interface TagSelectorProps {
  value: CommunicationTag[];
  onChange: (tags: CommunicationTag[]) => void;
  disabled?: boolean;
}

export function TagSelector({ value, onChange, disabled = false }: TagSelectorProps) {
  const toggleTag = (tag: CommunicationTag) => {
    if (disabled) return;

    if (value.includes(tag)) {
      onChange(value.filter((t) => t !== tag));
    } else {
      onChange([...value, tag]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {COMMUNICATION_TAGS.map((tag) => {
        const isSelected = value.includes(tag.value);
        return (
          <button
            key={tag.value}
            type="button"
            onClick={() => toggleTag(tag.value)}
            disabled={disabled}
            className={`
              px-3 py-1.5 text-sm font-medium rounded-full transition-all
              ${isSelected
                ? tag.color
                : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {tag.label}
          </button>
        );
      })}
    </div>
  );
}

interface TagDisplayProps {
  tags: CommunicationTag[];
  size?: 'sm' | 'md';
  maxDisplay?: number;
}

export function TagDisplay({ tags, size = 'sm', maxDisplay }: TagDisplayProps) {
  if (tags.length === 0) return null;

  const displayTags = maxDisplay ? tags.slice(0, maxDisplay) : tags;
  const remainingCount = maxDisplay ? tags.length - maxDisplay : 0;

  const sizeClasses = size === 'sm'
    ? 'px-2 py-0.5 text-xs'
    : 'px-3 py-1 text-sm';

  return (
    <div className="flex flex-wrap gap-1">
      {displayTags.map((tag) => {
        const config = COMMUNICATION_TAGS.find((t) => t.value === tag);
        if (!config) return null;

        return (
          <span
            key={tag}
            className={`${sizeClasses} font-medium rounded-full ${config.color}`}
          >
            {config.label}
          </span>
        );
      })}
      {remainingCount > 0 && (
        <span className={`${sizeClasses} font-medium rounded-full bg-secondary-100 text-secondary-600`}>
          +{remainingCount}
        </span>
      )}
    </div>
  );
}
